import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import type { Team } from "@redotech/redo-team-common/team";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  computeOverallProgress,
  getTaskDescription,
  isGuideComplete,
  isTaskActionable,
  isTaskComplete,
  isTaskVisible,
} from "../../registry/helpers";
import { getProductConfig } from "../../registry/product-registry";
import type {
  AsyncTaskStatuses,
  Product,
  SetupProgress,
  SetupTask,
} from "../../registry/types";
import * as css from "./setup-guide-widget.module.css";
import { deriveTaskStatus } from "./status-icon";
import { WidgetCompletionView } from "./widget-completion-view";
import { WidgetProgressBar } from "./widget-progress-bar";
import { WidgetTaskRow } from "./widget-task-row";

export interface SetupGuideWidgetProps {
  product: Product;
  progress: SetupProgress;
  asyncTaskStatuses?: AsyncTaskStatuses;
  /** Per-device hide while the merchant is still working through tasks.
   *  Header X button. Nav-pill brings it back. */
  onClose: () => void;
  /** Team-scoped permanent dismiss — only fires from the completion
   *  view's "Got it" button. Persists `dismissed_at` so the widget +
   *  nav-pill never return for anyone on the team. */
  onCompletionDismiss: () => void;
  /** Called before navigation so the provider can fire-and-forget the visit RPC. */
  onTaskClick: (taskId: string) => void;
}

// Same chargebacks hero used elsewhere in the Reclaim product (admin
// onboarding modal, dispute-sources card) so the widget feels at home.
const HEADER_IMAGE_URL =
  "https://redoapi-dev.s3.us-east-1.amazonaws.com/redo/static/onboarding-v2/onboarding-v2-chargebacks.webp";

/**
 * Bottom-right floating panel. Image banner + content body that
 * overlaps upward into the image with a rounded top edge — same recipe
 * as the Reclaim onboarding cards.
 */
export const SetupGuideWidget = memo(function SetupGuideWidget({
  product,
  progress,
  asyncTaskStatuses = {},
  onClose,
  onCompletionDismiss,
  onTaskClick,
}: SetupGuideWidgetProps) {
  const team = useContext(TeamContext);
  const config = getProductConfig(product);
  const navigate = useNavigate();

  const handleTaskClick = useCallback(
    (task: SetupTask, route: string) => {
      onTaskClick(task.id);
      if (!task.contextualModal) {
        navigate(route);
        return;
      }
      // URL param tells the page-level TaskContextualModalRenderer to open.
      const sep = route.includes("?") ? "&" : "?";
      navigate(`${route}${sep}setupTask=${encodeURIComponent(task.id)}`);
    },
    [onTaskClick, navigate],
  );

  // Flat ordered task list — flattens groups so the widget renders one
  // sequential accordion. The first actionable incomplete task is the
  // one that auto-expands when the widget mounts.
  const orderedTasks = useMemo<SetupTask[]>(() => {
    if (!config || !team) {
      return [];
    }
    return config.groups.flatMap((group) =>
      group.taskIds
        .map((taskId) => config.tasks[taskId])
        .filter((t): t is SetupTask => t != null && isTaskVisible(t, team)),
    );
  }, [config, team]);

  const firstActiveTaskId = useMemo<string | null>(() => {
    if (!config || !team) {
      return null;
    }
    for (const task of orderedTasks) {
      if (isTaskComplete(task, team, progress, asyncTaskStatuses)) {
        continue;
      }
      if (!isTaskActionable(task, config, team, progress, asyncTaskStatuses)) {
        continue;
      }
      return task.id;
    }
    return null;
  }, [orderedTasks, config, team, progress, asyncTaskStatuses]);

  // Detect the false→true completion edge so the celebration view fires
  // confetti exactly once per session. `prevCompleteRef` tracks the
  // previous render's completion state; when it flips we set
  // `justCompleted=true`, which renders <WidgetConfetti />. The flag
  // resets on dismiss (the widget closes), so re-opening later won't
  // re-fire confetti — completion view shows but stays calm.
  const isComplete =
    !!config &&
    !!team &&
    isGuideComplete(config, team, progress, asyncTaskStatuses);
  const prevCompleteRef = useRef(isComplete);
  const [justCompleted, setJustCompleted] = useState(false);
  useEffect(() => {
    if (!prevCompleteRef.current && isComplete) {
      setJustCompleted(true);
    }
    prevCompleteRef.current = isComplete;
  }, [isComplete]);

  if (!config || !team) {
    return null;
  }
  const overall = computeOverallProgress(
    config,
    team,
    progress,
    asyncTaskStatuses,
  );
  const teamId = team._id?.toString() ?? "";

  return (
    <div className={css.widget}>
      <div className={css.imageWrapper}>
        <img
          alt=""
          aria-hidden
          className={css.headerImage}
          src={HEADER_IMAGE_URL}
        />
        <div className={css.imageOverlay} />
        <div className={css.imageHeader}>
          <div className={css.imageHeaderText}>
            <span className={css.imageTitle}>Setup checklist</span>
          </div>
          <button
            aria-label="Close setup guide"
            className={css.imageButton}
            onClick={onClose}
            type="button"
          >
            <XCloseIcon className={css.imageButtonIcon} />
          </button>
        </div>
      </div>

      <div className={css.contentBody}>
        {isComplete ? (
          <WidgetCompletionView
            config={config}
            onDismiss={onCompletionDismiss}
            showConfetti={justCompleted}
          />
        ) : (
          <>
            <WidgetProgressBar progress={overall} />
            <div className={css.taskList}>
              {orderedTasks.map((task) => (
                <RenderedTaskRow
                  asyncTaskStatuses={asyncTaskStatuses}
                  autoExpand={task.id === firstActiveTaskId}
                  config={config}
                  key={task.id}
                  onClick={() => handleTaskClick(task, task.route(teamId))}
                  progress={progress}
                  task={task}
                  team={team}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
});

const RenderedTaskRow = memo(function RenderedTaskRow({
  task,
  team,
  progress,
  config,
  asyncTaskStatuses,
  onClick,
  autoExpand,
}: {
  task: SetupTask;
  team: Team;
  progress: SetupProgress;
  config: ReturnType<typeof getProductConfig>;
  asyncTaskStatuses: AsyncTaskStatuses;
  onClick: () => void;
  autoExpand: boolean;
}) {
  const status = deriveTaskStatus(task, team, progress, asyncTaskStatuses);
  const actionable = config
    ? isTaskActionable(task, config, team, progress, asyncTaskStatuses)
    : true;
  return (
    <WidgetTaskRow
      actionLabel={task.actionLabel}
      defaultExpanded={autoExpand}
      description={getTaskDescription(task, asyncTaskStatuses)}
      disabled={!actionable}
      onClick={onClick}
      status={status}
      title={task.title}
    />
  );
});
